import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDashboardBackgroundJobs } from "../../context/DashboardJobsContext";
import {
  formatDurationMs,
  formatBackgroundJobSubtitle,
  resolveBackgroundJobStatus,
  sanitizeBackgroundJobTitle,
} from "../../utils/dashboardJobLabels";
import { withDashboardNavigationNonce } from "../../utils/dashboardDeepLink";

export default function DashboardBackgroundJobsPanel() {
  const { t, i18n } = useTranslation("settings");
  const navigate = useNavigate();
  const { jobs, cancelJob, cancellingJobId } = useDashboardBackgroundJobs();
  const [expanded, setExpanded] = useState(true);

  if (!jobs.length) return null;

  const locale = i18n.language?.startsWith("en") ? "en-US" : "pt-BR";

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, next) => setExpanded(next)}
      sx={{ mb: 2, "&:before": { display: "none" } }}
      disableGutters
      elevation={0}
      variant="outlined"
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t("dashboardPage.backgroundJobs.title", {
              count: jobs.length,
              defaultValue: "Gerando relatórios ({{count}})",
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("dashboardPage.backgroundJobs.titleHint", {
              defaultValue: "Processamento em segundo plano — você pode continuar usando o sistema",
            })}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
          {t("dashboardPage.backgroundJobs.panelHint", {
            defaultValue:
              "Estamos montando os dados completos do período selecionado. Você pode continuar usando o sistema — avisaremos quando cada relatório ficar pronto. Os dados permanecerão disponíveis por pelo menos 24 horas para consultar, compartilhar ou exportar.",
          })}
        </Alert>
        <Stack spacing={2}>
          {jobs.map((job) => {
            const tabName = sanitizeBackgroundJobTitle(job.title, job.route);
            const subtitle = formatBackgroundJobSubtitle(job, locale);
            const elapsed = formatDurationMs(job.elapsedMs);
            const remaining = formatDurationMs(job.estimatedRemainingMs);
            const phase = resolveBackgroundJobStatus(job);
            const isCancelling = cancellingJobId === job.jobId;
            const statusLabel =
              job.status === "cancel_requested"
                ? t("dashboardPage.backgroundJobs.statusCancelling", {
                    defaultValue: "Cancelando…",
                  })
                : phase === "starting"
                  ? t("dashboardPage.backgroundJobs.statusStarting", {
                      defaultValue: "Iniciando geração…",
                    })
                  : t("dashboardPage.backgroundJobs.statusGenerating", {
                      defaultValue: "Gerando relatório…",
                    });

            return (
              <Box
                key={job.jobId}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t("dashboardPage.backgroundJobs.generatingTitle", {
                        tab: tabName,
                        defaultValue: "Gerando: {{tab}}",
                      })}
                    </Typography>
                    {subtitle ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t("dashboardPage.backgroundJobs.periodLabel", {
                          period: subtitle,
                          defaultValue: "Período {{period}}",
                        })}
                      </Typography>
                    ) : null}
                  </Box>
                  <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                    {statusLabel} · {job.progressPercent}%
                  </Typography>
                </Stack>

                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, job.progressPercent || 0))}
                  sx={{ mb: 1, height: 8, borderRadius: 1 }}
                />

                <Typography variant="caption" color="text.secondary" display="block">
                  {t("dashboardPage.backgroundJobs.timing", {
                    elapsed,
                    remaining,
                    defaultValue: "Gerando há {{elapsed}} · Previsão de conclusão: ~{{remaining}}",
                  })}
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }}>
                  {job.deepLink ? (
                    <Button
                      size="small"
                      sx={{ px: 0, alignSelf: "flex-start" }}
                      onClick={() => navigate(withDashboardNavigationNonce(job.deepLink))}
                    >
                      {t("dashboardPage.backgroundJobs.openTab", {
                        defaultValue: "Ir para a aba (visualização parcial disponível)",
                      })}
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    color="inherit"
                    disabled={isCancelling || job.status === "cancel_requested"}
                    onClick={() => void cancelJob(job.jobId)}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    {isCancelling || job.status === "cancel_requested"
                      ? t("dashboardPage.backgroundJobs.cancelling", { defaultValue: "Cancelando…" })
                      : t("dashboardPage.backgroundJobs.cancel", { defaultValue: "Interromper geração" })}
                  </Button>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
